import * as React from 'react';
import { MouseEventHandler, Ref } from 'react';
import PropTypes from 'prop-types';
import { MenuItem, MenuItemProps } from '@mui/material';
import inflection from 'inflection';
import {
    useTranslate,
    RedirectionSideEffect,
    Confirm,
    MutationMode,
    useRecordContext,
    useResourceContext,
} from 'react-admin';

import { useDeleteBranchWithConfirmController } from './controllers';
import { TreeRecord } from './types';

export const DeleteMenuItemWithConfirmation = <
    RecordType extends TreeRecord = TreeRecord
>(
    props: DeleteMenuItemWithConfirmationProps<RecordType>
) => {
    const {
        className,
        confirmTitle = 'ra.message.delete_title',
        confirmContent = 'ra.message.delete_content',
        label = 'ra.action.delete',
        mutationMode,
        onClick,
        record: recordProp,
        resource: resourceProp,
        ref,
        redirect: redirectTo = 'list',
        ...rest
    } = props;
    const resource = useResourceContext(props);
    const record = useRecordContext<RecordType>(props);
    const {
        handleDelete,
        handleDialogClose,
        handleDialogOpen,
        isLoading,
        open,
    } = useDeleteBranchWithConfirmController<RecordType>({
        mutationMode,
        record,
        resource,
        redirect: redirectTo,
    });
    const translate = useTranslate();

    const handleClick: MouseEventHandler<HTMLLIElement> = event => {
        event.stopPropagation();
        if (onClick) {
            onClick(event);
        }

        if (!event.isDefaultPrevented()) {
            handleDialogOpen(event);
        }
    };

    return (
        <>
            <MenuItem
                className={className}
                onClick={handleClick}
                ref={ref}
                {...rest}
            >
                {translate(label, { _: label })}
            </MenuItem>
            <Confirm
                isOpen={open}
                loading={isLoading}
                title={confirmTitle}
                content={confirmContent}
                translateOptions={{
                    name: inflection.humanize(
                        translate(`resources.${resource}.name`, {
                            smart_count: 1,
                            _: inflection.singularize(resource),
                        }),
                        true
                    ),
                    id: record.id,
                }}
                onConfirm={handleDelete}
                onClose={handleDialogClose}
            />
        </>
    );
};

export interface DeleteMenuItemWithConfirmationProps<
    RecordType extends TreeRecord = TreeRecord
> extends Omit<MenuItemProps, 'button' | 'id'> {
    confirmTitle?: string;
    confirmContent?: string;
    label?: string;
    mutationMode?: MutationMode;
    record?: RecordType;
    ref?: Ref<HTMLLIElement>;
    redirect?: RedirectionSideEffect;
    resource?: string;
}

DeleteMenuItemWithConfirmation.propTypes = {
    className: PropTypes.string,
    confirmTitle: PropTypes.string,
    confirmContent: PropTypes.string,
    label: PropTypes.string,
    mutationMode: PropTypes.oneOf(['pessimistic', 'optimistic', 'undoable']),
    record: PropTypes.any,
    redirect: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.func,
    ]),
    resource: PropTypes.string,
};
